// src/components/Payment.jsx
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearCart } from '../store/slices/cartSlice';
import { placeOrder } from '../store/slices/authslice';
import { toast } from 'react-toastify';
import { Copy } from 'lucide-react';

const Payment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { items } = useSelector((state) => state.cart);
  const { user, currentOrderDeliveryMethod } = useSelector((state) => state.auth);

  // Calculate subtotal from cart items (without delivery fee)
  const subtotal = items.reduce((total, item) => total + item.totalPrice, 0);

  // Bank details
  const accountNumber = '5727184210';
  const bankName = 'Moniepoint';
  const accountName = 'The Grill Shack Limited';

  // Text for full bank transfer details
  const bankDetailsText = `Account Number: ${accountNumber}
Bank: ${bankName}
Account Name: ${accountName}`;

  // Copy full bank details to clipboard
  const handleCopyDetails = async () => {
    try {
      await navigator.clipboard.writeText(bankDetailsText);
      toast.success('Bank details copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy bank details.');
    }
  };

  // Copy only the account number to clipboard
  const handleCopyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(accountNumber);
      toast.success('Account number copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy account number.');
    }
  };

  // Retrieve order details from localStorage (set by Cart component)
  const storedOrderDetails = JSON.parse(localStorage.getItem('orderDetails') || '{}');
  // Use stored finalTotal if available; otherwise fallback to subtotal
  const finalTotal = storedOrderDetails.finalTotal || subtotal;

  const handlePlaceOrder = async () => {
    try {
      if (!user?.uid) {
        toast.error('Please log in first');
        return;
      }

      // Use the stored order details from localStorage
      const orderDetails = storedOrderDetails;

      // Use finalTotal (which includes delivery fee) in the order data
      const orderData = {
        items: items.map(item => ({
          ...item,
          itemTotal: item.totalPrice
        })),
        totalAmount: finalTotal,
      };

      // Build the delivery method object from stored details
      const deliveryMethod = {
        type: currentOrderDeliveryMethod,
        contactPhone: orderDetails.contactPhone,
        differentPayer: orderDetails.differentPayer,
        payerName: orderDetails.payerName,
        paymentReference: `${user.username}`,
        deliveryAddress: orderDetails.deliveryAddress
      };

      const resultAction = await dispatch(placeOrder({ 
        orderData,
        deliveryMethod 
      }));

      if (placeOrder.fulfilled.match(resultAction)) {
        localStorage.removeItem('orderDetails');
        dispatch(clearCart());
        toast.success('Order placed successfully!');
        navigate('/orders');
      } else {
        throw new Error(resultAction.error.message);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-6 text-red-500">Payment Details</h2>

        {/* Bank Transfer Details Section */}
        <div className="max-w-md mx-auto bg-gray-800 p-6 rounded-lg shadow-lg mb-6 text-center">
          <h3 className="text-2xl font-semibold text-red-500 mb-4">Bank Transfer Details</h3>
          <div className="mb-4">
            <p className="text-lg mb-2">
              <span className="font-bold">Account Number:</span> {accountNumber}
            </p>
            <button 
              onClick={handleCopyAccountNumber}
              className="inline-flex items-center px-3 py-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-md text-sm"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Account Number
            </button>
          </div>
          <p className="text-lg mb-2">
            <span className="font-bold">Bank:</span> {bankName}
          </p>
          <p className="text-lg mb-4">
            <span className="font-bold">Account Name:</span> {accountName}
          </p>
          <button 
            onClick={handleCopyDetails}
            className="inline-flex items-center px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy All Details
          </button>
        </div>

        {/* Display totals */}
        <div className="text-center mb-6">
          <p className="text-lg font-bold">Subtotal: ₦{subtotal.toLocaleString()}</p>
          <p className="text-lg font-bold">Final Total (incl. Delivery): ₦{finalTotal.toLocaleString()}</p>
          <p className="text-sm text-gray-400">(Including extras, modifications, and delivery fee)</p>
        </div>

        <div className="flex justify-center space-x-4 mb-6">
          <button
            onClick={handlePlaceOrder}
            className="px-8 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
            disabled={!items.length}
          >
            I've Made the Transfer
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/cart')}
            className="px-8 py-3 bg-gray-700 text-white rounded-full hover:bg-gray-800 transition-colors shadow-lg"
          >
            Back to Cart
          </button>
        </div>

        {user && (
          <div className="mt-6 text-center">
            <a 
              href={`https://wa.me/+2347055859806?text=Hi, I've just made a payment for order ID: ${user.username}_${Date.now()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Contact us on WhatsApp
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;
